import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'large') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative rounded-2xl overflow-hidden h-[420px] cursor-pointer"
      >
        <img
          src={article.imageUrl}
          alt={article.title}
          title={`${article.title} - 暗网吃瓜`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-block px-3 py-1 bg-primary-500 text-background-950 text-xs font-medium rounded-full mb-3">
            {article.category}
          </span>
          <h3 className="text-foreground-50 text-xl md:text-2xl font-bold mb-2 line-clamp-2 group-hover:text-primary-400 transition-colors">
            {article.title}
          </h3>
          <p className="text-foreground-400 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-foreground-500 text-xs">
            <span className="flex items-center gap-1">
              <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.views.toLocaleString()}
            </span>
            <span>{new Date(article.publishedAt).toLocaleDateString('zh-CN')}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-24 h-24 rounded-lg overflow-hidden shrink-0">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 暗网吃瓜`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 flex-1">
          <div>
            <h4 className="text-foreground-200 text-sm font-medium line-clamp-2 group-hover:text-primary-400 transition-colors mb-1">
              {article.title}
            </h4>
            <p className="text-foreground-500 text-xs line-clamp-1">{article.excerpt}</p>
          </div>
          <div className="flex items-center gap-3 text-foreground-600 text-xs">
            <span>{article.category}</span>
            <span>{new Date(article.publishedAt).toLocaleDateString('zh-CN')}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl overflow-hidden bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={`${article.title} - 暗网吃瓜`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-100/80 via-transparent to-transparent"></div>
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 bg-primary-500 text-background-950 text-xs font-bold rounded-md flex items-center gap-1">
            <i className="ri-fire-fill w-3 h-3 flex items-center justify-center"></i>
            热门
          </span>
        )}
        <span className="absolute top-3 right-3 px-2 py-0.5 bg-background-950/70 backdrop-blur-sm text-foreground-300 text-xs rounded-md">
          {article.category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-foreground-100 font-semibold text-sm line-clamp-2 mb-2 group-hover:text-primary-400 transition-colors">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-xs line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-foreground-600 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full overflow-hidden">
              <img src={article.authorAvatar} alt={article.authorName} className="w-full h-full object-cover" />
            </div>
            <span>{article.authorName}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span>{new Date(article.publishedAt).toLocaleDateString('zh-CN')}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}